/**
 * UML State Machine Node Component
 * Renders a statechart state by `kind`:
 *   - simple / composite : rounded "stadium" box with the state name, optional
 *     description, and an internal-transitions compartment (entry/do/exit).
 *   - initial            : small solid dot.
 *   - final              : ringed dot (bullseye).
 *   - choice             : diamond.
 *   - fork / join        : thick bar.
 *
 * Transitions between states are ordinary edges labelled `event [guard] / action`.
 * Pseudostates expose minimal handles so transitions can attach cleanly.
 */

import { memo, useEffect } from 'react'
import { NodeProps, NodeResizer, useUpdateNodeInternals } from '@xyflow/react'
import type { StateContent } from '../../types/schema'
import { NodeConnectionHandles } from './NodeConnectionHandles'

interface StateNodeData {
  label: string
  stateContent?: StateContent
  style?: {
    bgColor?: string
    borderColor?: string
    textColor?: string
    headerBgColor?: string
    headerTextColor?: string
    borderWidth?: number
    fontSize?: number
    fontFamily?: string
  }
}

function StateNode({ id, data, selected, width, height }: NodeProps) {
  const nodeData = (data as unknown) as StateNodeData
  const sc = nodeData.stateContent
  const updateNodeInternals = useUpdateNodeInternals()
  useEffect(() => { updateNodeInternals(id) }, [id, updateNodeInternals])

  const kind = sc?.kind ?? 'simple'
  const accent = (nodeData as any).borderColor || nodeData.style?.borderColor || '#0ea5e9'
  const bodyBg = (nodeData as any).bgColor || nodeData.style?.bgColor || '#f0f9ff'
  const textColor = (nodeData as any).textColor || nodeData.style?.textColor || '#0c4a6e'
  const fontFamily = nodeData.style?.fontFamily || 'Inter, system-ui, sans-serif'

  // --- Pseudostate renderings (fixed small footprints) ---
  if (kind === 'initial') {
    return (
      <div style={{ width: 22, height: 22, borderRadius: '50%', background: '#0f172a' }} title="initial">
        <NodeConnectionHandles />
      </div>
    )
  }
  if (kind === 'final') {
    return (
      <div style={{ width: 24, height: 24, borderRadius: '50%', border: '2px solid #0f172a', display: 'flex', alignItems: 'center', justifyContent: 'center' }} title="final">
        <div style={{ width: 12, height: 12, borderRadius: '50%', background: '#0f172a' }} />
        <NodeConnectionHandles />
      </div>
    )
  }
  if (kind === 'choice') {
    return (
      <div style={{ width: 30, height: 30, background: bodyBg, border: `2px solid ${accent}`, transform: 'rotate(45deg)' }} title="choice">
        <div style={{ transform: 'rotate(-45deg)' }}><NodeConnectionHandles /></div>
      </div>
    )
  }
  if (kind === 'fork' || kind === 'join') {
    return (
      <div style={{ width: 60, height: 8, background: '#0f172a', borderRadius: 2 }} title={kind}>
        <NodeConnectionHandles />
      </div>
    )
  }

  // --- Simple / composite state box ---
  const nodeWidth = width || 160
  const nodeHeight = height || (sc?.internalTransitions?.length || sc?.description ? 110 : 56)
  const internal = sc?.internalTransitions ?? []

  return (
    <>
      {selected && <NodeResizer minWidth={120} minHeight={48} />}
      <div
        style={{
          width: nodeWidth,
          height: nodeHeight,
          borderRadius: 16, // stadium / rounded-rect per UML state convention
          border: `2px solid ${accent}`,
          background: bodyBg,
          color: textColor,
          fontFamily,
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
          boxShadow: '0 1px 3px rgba(0,0,0,0.08)',
        }}
      >
        <div
          style={{
            fontWeight: 600,
            fontSize: 13,
            textAlign: 'center',
            padding: '10px 12px',
            borderBottom: internal.length || sc?.description ? `1px solid ${accent}33` : 'none',
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
          }}
          title={sc?.stateName ?? nodeData.label}
        >
          {sc?.stateName ?? nodeData.label}
        </div>
        {sc?.description && (
          <div style={{ fontSize: 11, opacity: 0.8, padding: '6px 12px' }}>{sc.description}</div>
        )}
        {internal.length > 0 && (
          <div style={{ flex: 1, overflowY: 'auto', padding: '4px 12px', fontFamily: 'JetBrains Mono, monospace', fontSize: 11 }}>
            {internal.map((t, i) => (
              <div key={i} style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }} title={t}>{t}</div>
            ))}
          </div>
        )}
        <NodeConnectionHandles />
      </div>
    </>
  )
}

export default memo(StateNode)
